import {
  Button,
  ButtonVariant,
  FormMessage,
  FormMessageVariant,
  Input,
  InputGroup,
  Placement,
  type SelectOption,
  toast,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isEmpty, isNil, snakeCase, xor } from 'lodash';
import { MapTool } from 'map-3d/components/map-3d-container/map-3d-tools';
import { getEligibleLayerOptions } from 'map-3d/components/map-3d-sidebar/components/map-3d-workspace/shared/helpers';
import { PolygonEventType } from 'map-3d/shared';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3DState,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
} from 'map-3d/shared/map-3d-slices';
import { useEffect, useMemo, useState } from 'react';
import {
  FeatureCountLabel,
  FeatureGeometryType,
  type FeatureResponseData,
  FeatureType,
  type GenerateDEMRequestPayload,
  handleResponseErrorMessage,
  handleResponseSuccessMessage,
  useFeatureList,
  useGenerateDEMRequest,
  useIteration,
} from 'shared/api';
import { Placeholders } from 'shared/constants';
import { TerrainGenerationMessage } from 'shared/enums';
import { clampDecimalInput, preventNonNumber } from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import {
  FormErrorMessage,
  MarkArea,
  MarkAreaToggle,
  setVectorLayerVisibility,
  type WorkspaceLayer,
  type WorkspaceLayerListObj,
  zoomToLayer,
} from '../..';
import {
  type GenerateDEMFormType,
  type GenerateDEMInputType,
  type PointCloudWorkspaceLayer,
} from './types';
import { generateDEMValidator } from './validators';

// Constants.
const DEFAULT_OUTPUT_RESOLUTION = 0.15;
const OUTPUT_RESOLUTION_MIN = 0.01;
const OUTPUT_RESOLUTION_MAX = 5.0;

export const GenerateDEMForm: React.FC<GenerateDEMFormType> = ({
  visiblePointCloudLayers,
  setIsFormSubmitted,
}) => {
  // useStates.
  const [currentMarkedArea, setCurrentMarkedArea] = useState<MarkArea>(
    MarkArea.Draw,
  );
  const [isDrawPolygonToolEnabled, setIsDrawPolygonToolEnabled] =
    useState<boolean>(false);
  const [hasPolygons, setHasPolygons] = useState<boolean>(false);

  // Dispatch.
  const dispatch = useAppDispatch();

  // Selectors.
  const { workspaceLayers } = useAppSelector(selectMap3dWorkspace);
  const { cesiumProxy, aoiManager } = useAppSelector(selectMap3DState);

  const activeLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(
      state,
      FeatureGeometryType.Polygon,
    ),
  );

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  // useMemos.
  const pointCloudLayerOptions = useMemo(
    () =>
      Object.values(visiblePointCloudLayers).map((layer: WorkspaceLayer) => ({
        label: layer.name,
        value: {
          ...layer,
          tooltipText: layer.canManageLayer
            ? null
            : `You don't have create & manage permissions for the parent site of ${layer.name}`,
        },
      })),
    [visiblePointCloudLayers],
  );

  const visibleWorkspaceLayers: WorkspaceLayerListObj = useMemo(() => {
    return Object.fromEntries(
      Object.entries(workspaceLayers).filter(([, layer]) => layer.show),
    );
  }, [workspaceLayers]);

  const polygonLayersSelectOptions: SelectOption<WorkspaceLayer>[] =
    useMemo(() => {
      return getEligibleLayerOptions(
        activeLayers,
        FeatureCountLabel.Polygons,
        1,
      );
    }, [activeLayers]);

  // Constants.
  const generateDEMInitialState = {
    pointCloudLayer:
      pointCloudLayerOptions.find(
        (pointCloudLayerOption) => pointCloudLayerOption.value.canManageLayer,
      ) ?? null,
    outputResolution: DEFAULT_OUTPUT_RESOLUTION,
    workspaceLayer: null,
  };

  // Hooks.
  const {
    names,
    values,
    errors,
    setValues,
    setErrors,
    onBlur,
    onFocus,
    inputHasError,
  } = useInputFields<GenerateDEMInputType>(
    generateDEMInitialState,
    generateDEMValidator,
  );

  // Variables.
  const { pointCloudLayer, outputResolution, workspaceLayer } = values;

  const canManageLayer = pointCloudLayer?.value.canManageLayer ?? false;

  const siteName = canManageLayer ? pointCloudLayer?.value.site.name : null;

  const iterationName = canManageLayer
    ? pointCloudLayer?.value.iteration.name
    : null;

  const isGenerateDEMDisabled =
    isNil(pointCloudLayer) ||
    isNil(outputResolution) ||
    inputHasError() ||
    (currentMarkedArea === MarkArea.Draw && !hasPolygons) ||
    (currentMarkedArea === MarkArea.LayerFromWorkspace && !workspaceLayer);

  const selectedLayerIterationId =
    pointCloudLayer?.value?.iteration?.id ?? null;

  // API.
  const {
    data: featuresResponse,
    isFetching: isLoadingFeaturesResponse,
    isSuccess: isSuccessFeaturesResponse,
    isError: isErrorFeaturesResponse,
    error: featuresResponseError,
    refetch: refetchFeatures,
  } = useFeatureList(
    workspaceLayer?.value.id ?? null,
    FeatureType.Polygon,
    false,
  );

  const {
    mutate: submitGenerateDEMRequest,
    data: generateDEMResponse,
    isPending: isGenerateDEMLoading,
    isError: isGenerateDEMError,
    isSuccess: isGenerateDEMSuccess,
  } = useGenerateDEMRequest();

  const { data: iterationResponse } = useIteration(
    selectedLayerIterationId ?? '',
    false,
    !isNil(selectedLayerIterationId),
  );

  const selectedLayerHasCapturedDEM =
    !isNil(iterationResponse?.data.capturedDSMCog?.id) ||
    !isNil(iterationResponse?.data.capturedDSM?.id);

  // useEffects.
  useEffect(() => {
    return () => {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
    };
  }, []);

  useEffect(() => {
    polygonTool?.toggleClampTo3DTileset(true);

    return () => {
      polygonTool?.toggleClampTo3DTileset(false);
    };
  }, [polygonTool]);

  useEffect(() => {
    if (isSuccessFeaturesResponse && !isNil(featuresResponse)) {
      loadPolygonsFromWorkspace(featuresResponse.data.features);
    }

    handleResponseErrorMessage(isErrorFeaturesResponse, featuresResponseError);
  }, [
    isSuccessFeaturesResponse,
    featuresResponse,
    isErrorFeaturesResponse,
    featuresResponseError,
  ]);

  useEffect(() => {
    if (isNil(workspaceLayer)) {
      return;
    }

    refetchFeatures();

    // Clear all drawn polygons.
    deleteDEMPolygonsByMarkedArea(MarkArea.Draw);

    /*
     Clear all polygons from previously selected layers.
     (excluding the current selected layers polygons)
     */
    const features = workspaceLayer?.value.features;
    const selectedDEMPolygonIds: string[] | undefined = features?.map(
      (feature) => feature.id,
    );

    deleteDEMPolygonsByMarkedArea(
      MarkArea.LayerFromWorkspace,
      selectedDEMPolygonIds,
    );
  }, [workspaceLayer]);

  useEffect(() => {
    if (isNil(cesiumProxy) || isNil(pointCloudLayer) || !canManageLayer) {
      return;
    }

    zoomToLayer(pointCloudLayer.value, cesiumProxy, pointCloudLayer.value.site);
  }, [pointCloudLayer?.value?.id, cesiumProxy]);

  useEffect(() => {
    if (isGenerateDEMSuccess) {
      setIsFormSubmitted(true);
      handleResponseSuccessMessage(isGenerateDEMSuccess, generateDEMResponse);
    }

    if (isGenerateDEMError) {
      toast.error('Failed to generate DEM. Please try again.');
    }
  }, [isGenerateDEMSuccess, isGenerateDEMError]);

  // Handlers.
  const onChangeLayer = (
    selectedOption: SelectOption<PointCloudWorkspaceLayer>,
  ) => {
    setValues((values) => ({
      ...values,
      pointCloudLayer: selectedOption,
    }));
  };

  const onToggleChange = (toggledLabel: string) => {
    const toggledValue = snakeCase(toggledLabel) as MarkArea;

    if (toggledValue === MarkArea.Draw) {
      deleteDEMPolygonsByMarkedArea(MarkArea.LayerFromWorkspace);
      setVectorLayerVisibility(visibleWorkspaceLayers, true);

      setErrors((errors) => ({
        ...errors,
        workspaceLayer: '',
      }));

      setValues((values) => ({ ...values, workspaceLayer: null }));
    } else {
      setVectorLayerVisibility(workspaceLayers, false);
      deleteDEMPolygonsByMarkedArea(MarkArea.Draw);
      setIsDrawPolygonToolEnabled(false);

      polygonTool?.deactivate();

      if (
        isNil(polygonLayersSelectOptions) ||
        isEmpty(polygonLayersSelectOptions)
      ) {
        setErrors((errors) => ({
          ...errors,
          workspaceLayer: FormErrorMessage.NoActivePolygonLayer,
        }));
      }
    }

    setCurrentMarkedArea(toggledValue);
  };

  const loadPolygonsFromWorkspace = (
    featureResponse: FeatureResponseData[],
  ) => {
    if (isNil(polygonTool) || isEmpty(featureResponse)) {
      return;
    }

    featureResponse.forEach((feature) => {
      if (feature.type === FeatureType.Polygon) {
        polygonTool?.importWKT(
          feature.geometry,
          feature.id,
          { demPolygons: MarkArea.LayerFromWorkspace },
          feature.name,
        );
      }
    });
  };

  const deleteDEMPolygonsByMarkedArea = (
    markArea: MarkArea,
    excludePolygons?: string[],
  ) => {
    const demPolygonsIds = polygonTool
      ?.getPolygonByProperty('demPolygons', markArea)
      .map((polygon) => {
        return polygon.id;
      });

    const polygonsToDelete = xor(demPolygonsIds, excludePolygons);

    for (const polygon of polygonsToDelete) {
      polygonTool?.deleteById(polygon);
    }

    polygonTool?.resetCurrentDrawnArray(false);
  };

  const polygonCreatedHandler = () => {
    setHasPolygons(true);
  };

  const startDrawingHandler = () => {
    polygonTool?.deleteAllPolygons();

    polygonTool?.activate(
      {
        demPolygons: MarkArea.Draw,
      },
      {},
      true,
    );

    polygonTool?.addEventListener(
      PolygonEventType.PolygonCreated,
      polygonCreatedHandler,
    );

    dispatch(setCurrentActiveMapTool(MapTool.None));
    setIsDrawPolygonToolEnabled(true);
  };

  const endDrawingHandler = () => {
    polygonTool?.deactivate();
    polygonTool?.removeEventListener(
      PolygonEventType.PolygonCreated,
      polygonCreatedHandler,
    );

    setIsDrawPolygonToolEnabled(false);
  };

  const resetDrawingHandler = () => {
    polygonTool?.deleteAllCurrentPolygons();
    setHasPolygons(false);

    polygonTool?.activate(
      {
        demPolygons: MarkArea.Draw,
      },
      {},
      true,
    );

    setIsDrawPolygonToolEnabled(true);
  };

  const onChangeOutputResolution = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) =>
    setValues((values) => ({
      ...values,
      outputResolution: clampDecimalInput(event.target.value ?? '', 2, false),
    }));

  const onChangeWorkspaceLayer = (
    selectedOption: SelectOption<WorkspaceLayer>,
  ) =>
    setValues((values) => ({
      ...values,
      workspaceLayer: selectedOption,
    }));

  const getClippingPolygonWkt = (): string | null => {
    const polygonIds = polygonTool?.currentDrawnPolygonIds ?? [];

    if (polygonIds.length !== 1) {
      toast.error('Please draw one polygon.');
      return null;
    }

    const polygon = polygonTool?.currentDrawnPolygons?.[polygonIds[0]];

    if (isNil(polygon)) {
      toast.error('Failed to retrieve polygon.');
      return null;
    }

    return polygon.exportWKT().wkt;
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isNil(pointCloudLayer)) {
      toast.error('Please select a point cloud layer and a clipping polygon.');
      return;
    }

    const generateDEMRequestPayload: GenerateDEMRequestPayload = {
      pointCloudLayerId: pointCloudLayer.value.id,
      outputResolution: Number(outputResolution),
    };

    if (currentMarkedArea === MarkArea.LayerFromWorkspace) {
      if (isNil(workspaceLayer?.value?.id)) {
        toast.error('Please select a clipping polygon layer.');
        return;
      }

      generateDEMRequestPayload.clippingPolygonLayerId =
        workspaceLayer.value.id;
    } else {
      const clippingPolygonWkt = getClippingPolygonWkt();

      if (isNil(clippingPolygonWkt)) {
        return;
      }

      generateDEMRequestPayload.clippingPolygonWkt = clippingPolygonWkt;
    }

    submitGenerateDEMRequest(generateDEMRequestPayload);
  };

  return (
    <form className="generate-dem__form" onSubmit={onSubmit}>
      <div className="generate-dem__form-container">
        <div className="generate-dem__form__section">
          <div className="generate-dem__form__section-title">
            Point Cloud Details
          </div>

          <InputGroup>
            <Input.Label isRequired> Select Layer</Input.Label>

            <Input.Select
              placeholder={Placeholders.SelectLayerFromWorkspace}
              options={pointCloudLayerOptions}
              name={names.pointCloudLayer}
              value={pointCloudLayer}
              isDisabled={
                isEmpty(pointCloudLayerOptions) ||
                !canManageLayer ||
                isGenerateDEMLoading
              }
              isOptionDisabled={(option: SelectOption<WorkspaceLayer>) =>
                !option.value.canManageLayer
              }
              onChange={onChangeLayer}
            />
          </InputGroup>
        </div>

        <div className="generate-dem__form__context">
          {!isNil(siteName) && !isNil(iterationName) ? (
            <>
              <div className="generate-dem__form__context-item">
                <span className="generate-dem__form__context-label">Site:</span>

                <span className="generate-dem__form__context-value">
                  {siteName}
                </span>
              </div>

              <div className="generate-dem__form__context-item">
                <span className="generate-dem__form__context-label">
                  Iteration:
                </span>

                <span className="generate-dem__form__context-value">
                  {iterationName}
                </span>
              </div>
            </>
          ) : (
            <FormMessage
              variant={FormMessageVariant.Error}
              message={TerrainGenerationMessage.NoPointCloudLayerError}
            />
          )}
        </div>

        <InputGroup>
          <Input.Label
            isRequired
            info="Defines how detailed the output will be. Lower values give more detail but take longer to generate."
            infoPlacement={Placement.Top}
          >
            Output Resolution
            <span className="generate-dem__form__output-resolution__title-unit">
              (m)
            </span>
          </Input.Label>

          <Input.Number
            name={names.outputResolution}
            value={outputResolution ?? ''}
            placeholder={`${DEFAULT_OUTPUT_RESOLUTION}`}
            min={OUTPUT_RESOLUTION_MIN}
            max={OUTPUT_RESOLUTION_MAX}
            step="any"
            onKeyDown={preventNonNumber}
            isInvalid={!!errors.outputResolution}
            error={errors.outputResolution}
            disabled={isGenerateDEMLoading}
            onChange={onChangeOutputResolution}
            {...{ onBlur, onFocus }}
          />
        </InputGroup>

        <MarkAreaToggle
          headerComponent={
            <Input.Label
              className="mark-area__heading"
              isRequired
              info="Defines the area for output generation. Anything outside this boundary is not included."
              infoPlacement={Placement.Top}
            >
              Clipping Boundary
            </Input.Label>
          }
          isDisabled={isGenerateDEMLoading}
          currentMarkedArea={currentMarkedArea}
          onToggleChange={onToggleChange}
          drawingHandlers={{
            endDrawingHandler,
            startDrawingHandler,
            resetDrawingHandler,
          }}
          isDrawingToolEnabled={isDrawPolygonToolEnabled}
          workspaceLayerError={errors.workspaceLayer}
          selectLayerSelectProps={{
            options: polygonLayersSelectOptions,
            value: workspaceLayer,
            name: names.workspaceLayer,
            isDisabled:
              isEmpty(polygonLayersSelectOptions) || isGenerateDEMLoading,
            isLoading: isLoadingFeaturesResponse,
            onChange: onChangeWorkspaceLayer,
            onBlur,
            onFocus,
          }}
        />

        <FormMessage
          className="generate-dem__form-message"
          message={
            selectedLayerHasCapturedDEM
              ? TerrainGenerationMessage.HasCapturedDEM
              : TerrainGenerationMessage.NoCapturedDEM
          }
          variant={
            selectedLayerHasCapturedDEM
              ? FormMessageVariant.Warning
              : FormMessageVariant.Info
          }
        />
      </div>

      <div className="generate-dem__form__footer">
        <Button
          variant={ButtonVariant.Primary}
          type="submit"
          disabled={isGenerateDEMDisabled}
        >
          {isGenerateDEMLoading ? 'Generating DEM' : 'Generate DEM'}
        </Button>
      </div>
    </form>
  );
};
